import { useMemo } from "react";
import Attachment from "../common/components/Attachment";
import DataTable from "../common/components/DataTable";
import useNetwork from "../common/hooks/networkHook";
import { addRelatedDoc, removeRelatedDoc, useJobRelatedDocs } from "../reducers/jobRelatedDocSlice";
import PropTypes from 'prop-types';
import { useDocTypes } from "../reducers/docTypeSlice";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";
import { useJob } from "../reducers/jobSlice";

const defaultSort = ['jdocid'];

/**
 * A component which displays a job's related docs and allows attachments to be uploaded and downloaded
 * @component
 */
const JobRelatedDocs = ({ jobid, disabled = false, style }) => {
    const job = useJob(jobid);
    const { t } = useSpecializedTranslation(job);
    const network = useNetwork();
    const docs = useJobRelatedDocs(jobid);
    const doctypes = useDocTypes(job?.site, job?.level, job?.type);
    const footerRowDef = useMemo(() => [
        { type: "typeahead", field: "doctype", required: true, editable: true, title: t('docType', { count: 1 }), suggestions: doctypes.map(dt => dt.name) },
        { type: "url", field: "details", required: true, editable: true, title: t('url') + ' / ' + t('details') },
        { type: "text", field: "notes", editable: true, title: t('notes') },
        { type: "label", text: " ", title: t('attachment_one'), uuid: 'attachment' }
    ], [t, doctypes]);
    
    const rowDef = useMemo(() => [...footerRowDef.map(c => ({ ...c, readonly: true, editable: false })).slice(0, -1), { title: t('attachment_one'), type: "dynamic", key: 'attachment', editable: true, readonly: false, def: (value, disabled, hideLabel, onChange) => {
        return <div className="w-100">
            <Attachment disabled={disabled} srcURL={value.file ? '/api/v1/jobRelatedDoc/' + encodeURIComponent(value.jdocid) + '/file' : undefined} destURL={value.file ? undefined : '/api/v1/jobRelatedDoc/' + encodeURIComponent(value.jdocid) + '/file' }></Attachment>
        </div> 
        } }], [t, footerRowDef]);

    return <DataTable value={docs} columns={rowDef} footerColumns={footerRowDef} emptyMessage={t('emptyJobRelatedContent')} defaultSort={defaultSort}
            confirmDelete={doc => t('confirmDeleteItem', { item: doc.doctype + (doc.notes ? ' - ' + doc.notes : '') })}
            onAdd={doc => addRelatedDoc(network, {...doc, jobid})} onRemove={doc => removeRelatedDoc(network, doc)} disabled={disabled} style={style}></DataTable>
}

JobRelatedDocs.propTypes = {
    /**
     * The jobid whose related documents are to be rendered
     */
    jobid: PropTypes.string,
    
    /**
     * Disables the ability to upload documents as well as to add and remove entries
     */
    disabled: PropTypes.bool
}

export default JobRelatedDocs;